/**
 * Deposit Scheme Account Slide-over Alpine.js Component
 *
 * Simple account slideover showing balance and transactions
 * for a deposit protection scheme (e.g. DPS, TDS, MyDeposits).
 */

import { transactionSearchMixin } from './transaction_search_mixin'

const PLURALS = { deposit_scheme: 'deposit_schemes' }

export function depositSchemeAccountSlideover() {
  const searchMixin = transactionSearchMixin('deposit_scheme')

  return {
    ...searchMixin,

    open: false,
    loading: false,
    loadingMore: false,
    error: null,
    zIndex: 100010,

    schemeId: null,
    scheme: null,
    stats: null,
    transactions: [],

    page: 1,
    hasMore: false,

    openSlideover(detail) {
      const id = detail?.id
      if (!id) return

      const store = Alpine.store?.('slideovers')
      if (store?.isOpen?.('deposit-scheme-account', id)) {
        store.bringToFront('deposit-scheme-account', id)
        return
      }
      if (store?.getNextZIndex) {
        this.zIndex = store.getNextZIndex()
      }

      this.schemeId = id
      this.open = true
      this.transactions = []
      this.page = 1
      this.hasMore = false
      this.clearSearch()

      if (store?.register) {
        store.register('deposit-scheme-account', id, this)
      }

      this.fetchData(true)
    },

    close() {
      const store = Alpine.store?.('slideovers')
      if (store && this.schemeId) {
        store.unregister('deposit-scheme-account', this.schemeId)
      }
      this.$nextTick(() => {
        this.open = false
        window.dispatchEvent(new CustomEvent('account-slideover-closed'))
      })
    },

    async fetchData(initial = false) {
      if (initial) {
        this.loading = true
        this.page = 1
        this.transactions = []
      } else {
        this.loadingMore = true
      }
      this.error = null

      try {
        const params = new URLSearchParams()
        params.set('page', this.page)

        const response = await fetch(`/accounting/accounts/deposit_schemes/${this.schemeId}.json?${params}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) throw new Error(`HTTP ${response.status}`)

        const data = await response.json()
        this.scheme = data.scheme
        this.stats = data.stats

        if (initial) {
          this.transactions = data.transactions?.lines || []
        } else {
          this.transactions = [...this.transactions, ...(data.transactions?.lines || [])]
        }

        this.hasMore = data.transactions?.pagination?.has_more || false
      } catch (error) {
        console.error('Failed to fetch deposit scheme account:', error)
        this.error = 'Failed to load account details.'
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    loadMore() {
      if (!this.hasMore || this.loadingMore) return
      this.page++
      this.fetchData(false)
    },

    // Formatting helpers (match account_tab_mixin)
    formatPence(pence) {
      if (pence == null) return '—'
      const abs = Math.abs(pence)
      const str = (abs / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      return pence < 0 ? `-£${str}` : `£${str}`
    },

    formatSignedPence(pence) {
      if (pence == null) return '—'
      const abs = Math.abs(pence)
      const str = (abs / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      if (pence > 0) return `+£${str}`
      if (pence < 0) return `-£${str}`
      return `£${str}`
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr + 'T00:00:00')
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    },

    txnBarClass(txn) {
      if (txn.amount_pence > 0) return 'bg-green-400'
      if (txn.amount_pence < 0) return 'bg-red-400'
      return 'bg-gray-300'
    },

    amountColorClass(pence) {
      if (pence > 0) return 'text-green-600 dark:text-green-400'
      if (pence < 0) return 'text-red-600 dark:text-red-400'
      return 'text-gray-500'
    },

    // Required by search mixin
    _searchAccountableId() {
      return this.schemeId
    }
  }
}
